/**
 * The old Bands table, for a line whose bands are **not stacked** tiers
 * (`builder-engine-and-consistency.md` §6.8 item 5, Decision D6).
 *
 * Somebody typed those bases by hand, so `bandsToTiers` cannot read them as
 * tiers and nothing converts them behind anyone's back: the line keeps this
 * table, unchanged, with its warning, until the quoter presses Remove tiers.
 * Moved here from the old catalog service flyout at step 9, when the
 * catalog's service editor became the pop-out.
 */
import { useMemo } from 'react'
import { bandsAreMonotonic, type QuantityRule } from '@rowted/shared'
import { Button, SectionLabel } from '../../components/primitives.js'
import { formatNumber } from '../../lib/format.js'
import { cn } from '../../lib/cn.js'
import { TIERS_REMOVE, TIERS_TITLE } from '../copy.js'

/** One band as the table holds it — strings while typed. */
export interface LegacyBand {
  minMeasure: string | number
  baseQty: string | number
  qtyPerUnit: string | number
}

/**
 * The bands over one line's multiplier, and the warning that matters.
 *
 * A band carries its own base and conversion, and starts at a floor with no
 * ceiling — it runs to the next band's floor, so a gap cannot be typed.
 *
 * **The warning is the point of this panel.** Lowering the conversion in a
 * higher band without raising its base makes the bigger job cheaper: for the
 * total not to drop at a floor, the next band's base has to make up what the
 * lower rate gives away. It warns rather than refuses, because a genuine
 * discount threshold is somebody's decision to make — but an accidental one
 * should never reach a quote unseen.
 */
export function LegacyBands({
  line, unit, onChange,
}: {
  line: { baseQty: string | number; qtyPerUnit: string | number; bands: LegacyBand[] }
  /** The measurement's unit — "sq ft" — so a floor reads as a quantity. */
  unit: string
  onChange: (bands: LegacyBand[] | null) => void
}) {
  const rule: QuantityRule = useMemo(() => ({
    baseQty: Number(line.baseQty) || 0,
    qtyPerUnit: Number(line.qtyPerUnit) || 0,
    bands: line.bands.map((band) => ({
      minMeasure: Number(band.minMeasure) || 0,
      baseQty: Number(band.baseQty) || 0,
      qtyPerUnit: Number(band.qtyPerUnit) || 0,
    })),
  }), [line.baseQty, line.qtyPerUnit, line.bands])

  const verdict = useMemo(() => bandsAreMonotonic(rule), [rule])

  const setBand = (index: number, changes: Partial<LegacyBand>) =>
    onChange(line.bands.map((band, at) => (at === index ? { ...band, ...changes } : band)))

  return (
    <div className="flex flex-col gap-2 rounded-field bg-surface-alt p-2.5">
      <div className="flex items-center gap-2">
        <b className="text-[14px] font-bold text-text">{TIERS_TITLE}</b>
        <button type="button" onClick={() => onChange(null)}
          className="ml-auto text-[12.5px] font-semibold text-danger">
          {TIERS_REMOVE}
        </button>
      </div>
      <div className="flex items-baseline gap-2">
        <SectionLabel>Bands</SectionLabel>
        <span className="text-sub text-text-faint">
          Each one starts where the last ends.
        </span>
      </div>

      {line.bands.length === 0
        ? (
          <p className="m-0 text-sub text-text-muted">
            No bands — the two numbers above apply at every {unit || 'measure'}.
          </p>
        )
        : (
          <table className="w-full border-collapse text-body">
            <thead>
              <tr className="text-left">
                <th className="py-1 text-label text-text-muted">FROM ({unit || 'measure'})</th>
                <th className="py-1 text-right text-label text-text-muted">BASE</th>
                <th className="py-1 text-right text-label text-text-muted">
                  PER {(unit || 'measure').toUpperCase()}
                </th>
                <th className="w-[32px]" />
              </tr>
            </thead>
            <tbody>
              {line.bands.map((band, index) => (
                <tr key={index}>
                  <td className="py-1 pr-1.5">
                    <input
                      inputMode="decimal"
                      aria-label={`Band ${index + 1} starts at`}
                      value={String(band.minMeasure)}
                      onChange={(event) =>
                        setBand(index, { minMeasure: event.currentTarget.value })}
                      className={BAND_INPUT}
                    />
                  </td>
                  <td className="py-1 pr-1.5">
                    <input
                      inputMode="decimal"
                      aria-label={`Band ${index + 1} base`}
                      value={String(band.baseQty)}
                      onChange={(event) => setBand(index, { baseQty: event.currentTarget.value })}
                      className={cn(BAND_INPUT, 'text-right')}
                    />
                  </td>
                  <td className="py-1 pr-1.5">
                    <input
                      inputMode="decimal"
                      aria-label={`Band ${index + 1} per ${unit || 'measure'}`}
                      value={String(band.qtyPerUnit)}
                      onChange={(event) =>
                        setBand(index, { qtyPerUnit: event.currentTarget.value })}
                      className={cn(BAND_INPUT, 'text-right')}
                    />
                  </td>
                  <td className="py-1 text-right">
                    <Button variant="ghost" aria-label={`Remove band ${index + 1}`}
                      onClick={() => onChange(line.bands.filter((_, at) => at !== index))}>
                      ×
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

      {!verdict.ok && (
        <p className={cn('m-0 rounded-field border border-warn bg-warn-bg px-2 py-1.5',
          'text-sub text-warn')}>
          At {formatNumber(verdict.at)} {unit} this drops from{' '}
          <b>{formatNumber(verdict.before)}</b> to <b>{formatNumber(verdict.after)}</b> — a
          bigger job would cost less than a smaller one. Raise that band&rsquo;s base by{' '}
          <b>{formatNumber(Math.round((verdict.before - verdict.after) * 1000) / 1000)}</b> to
          make it carry across.
        </p>
      )}

      <div>
        <Button variant="ghost" onClick={() => {
          /*
           * A new band starts above the highest floor so far and opens on the
           * numbers in force there, which is the edit somebody is about to
           * make rather than a row of zeroes they have to retype.
           */
          const floors = line.bands.map((band) => Number(band.minMeasure) || 0)
          const top = floors.length > 0 ? Math.max(...floors) : null
          const from = top === null ? 0 : top + 1000
          const carried = top === null
            ? { baseQty: line.baseQty, qtyPerUnit: line.qtyPerUnit }
            : line.bands[floors.indexOf(top)]!
          onChange([...line.bands, {
            minMeasure: String(from),
            baseQty: String(carried.baseQty),
            qtyPerUnit: String(carried.qtyPerUnit),
          }])
        }}>
          + Band
        </Button>
      </div>
    </div>
  )
}

const BAND_INPUT = 'h-8 w-full rounded-field border border-line bg-surface px-1.5 tnum '
  + 'text-cell text-text'
